const stableStringify = (value) => {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  if (value && typeof value === 'object' && !(value instanceof Date)) {
    return `{${Object.keys(value)
      .filter((key) => value[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value) ?? '';
};

const toPlainHeaders = (headers) => {
  if (!headers) return {};
  return typeof headers.toJSON === 'function' ? headers.toJSON() : headers;
};

const getAuthorization = (instance, headers) => {
  const requestHeaders = toPlainHeaders(headers);
  return (
    requestHeaders.Authorization ??
    requestHeaders.authorization ??
    instance.defaults.headers.common?.Authorization ??
    instance.defaults.headers.Authorization ??
    ''
  );
};

const cloneData = (data) => {
  if (data === null || typeof data !== 'object') return data;
  if (typeof structuredClone === 'function') return structuredClone(data);
  return JSON.parse(JSON.stringify(data));
};

const cloneResponse = (response) =>
  response ? {...response, data: cloneData(response.data)} : response;

const canDedupe = (config) =>
  config.dedupe !== false &&
  !config.signal &&
  !config.cancelToken &&
  (!config.responseType || config.responseType === 'json');

const WRITE_METHODS = ['post', 'put', 'patch', 'delete'];

/**
 * Shares identical in-flight GET requests: while a request is pending, identical
 * calls receive its result (with their own copy of `data`). Settled requests are
 * forgotten, so this is not a cache. Pass `{dedupe: false}` to always send a new request.
 * Any write request clears the pending ones, so a GET sent after a save never
 * joins a GET that started before it and returns the old data.
 * @param instance - The axios instance whose `get` method is wrapped.
 * @param getExtraKey - Returns extra key parts for values added later by request interceptors.
 */
export const dedupeGetRequests = (instance, getExtraKey = () => '') => {
  const inFlight = new Map();
  const originalGet = instance.get.bind(instance);

  WRITE_METHODS.forEach((method) => {
    const originalWrite = instance[method].bind(instance);
    instance[method] = (...args) => {
      inFlight.clear();
      return originalWrite(...args);
    };
  });

  instance.get = (url, config = {}) => {
    const {dedupe, ...requestConfig} = config;
    if (!canDedupe(config)) {
      return originalGet(url, requestConfig);
    }

    const {Authorization, authorization, ...otherHeaders} = toPlainHeaders(
      requestConfig.headers,
    );
    const key = stableStringify([
      instance.getUri({url, baseURL: requestConfig.baseURL}),
      requestConfig.params ?? null,
      getAuthorization(instance, requestConfig.headers),
      otherHeaders,
      getExtraKey(),
    ]);

    const pending = inFlight.get(key);
    if (pending) {
      pending.sharers += 1;
      return pending.request.then((response) =>
        cloneResponse(pending.snapshot ?? response),
      );
    }

    // The request is forgotten as soon as it settles, so no caller can join
    // after this point. The snapshot is taken before any caller's handlers run,
    // so a caller that mutates its response cannot change what the others receive.
    const entry = {sharers: 0, snapshot: null};
    const forget = () => {
      if (inFlight.get(key) === entry) {
        inFlight.delete(key);
      }
    };
    entry.request = originalGet(url, requestConfig).then(
      (response) => {
        forget();
        if (entry.sharers > 0) entry.snapshot = cloneResponse(response);
        return response;
      },
      (error) => {
        forget();
        throw error;
      },
    );
    inFlight.set(key, entry);
    return entry.request;
  };
};
